import { useCallback } from 'react';

/**
 * Shared utility function for making API calls with consistent error handling
 * @param {string} url - The API endpoint URL
 * @param {string} accessToken - The authorization bearer token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<{success: boolean, response?: Response, error?: string}>}
 */
export const makeApiCall = async (url, accessToken, options = {}) => {
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { success: true, response };
  } catch (error) {
    console.error("API call failed:", error);
    
    const errorMessage = error.message.includes('HTTP error! status:') 
      ? `Server error: ${error.message.replace('HTTP error! status:', 'Status')}`
      : "Network error. Please check your connection and try again.";
    
    return { success: false, error: errorMessage };
  }
};

/**
 * Hook version of makeApiCall that uses useCallback for React components
 * @param {string} accessToken - The authorization bearer token
 * @returns {Function} - Memoized API call function
 */
export const useApiCall = (accessToken) => {
  return useCallback(async (url, options = {}) => {
    return makeApiCall(url, accessToken, options);
  }, [accessToken]);
};